import {
  format,
  formatDistanceToNow,
  formatRelative,
  isValid,
  parseISO,
} from 'date-fns'

const TIMEZONE_SUFFIX = 'Z'

export const getDateFromStr = (dateStr: string | null) => {
  return dateStr ? new Date(dateStr) : null
}

export const isValidISOString = (input: string) => isValid(parseISO(input))

export const formatISO = (date: string | Date, dateFormat: string) =>
  date instanceof Date
    ? format(date, dateFormat)
    : format(parseISO(date), dateFormat)

export const formatISOToLongDateTime = (date: string) => formatISO(date, 'PPpp')

export const formatISOToShortDateTime = (date: string) => formatISO(date, 'Pp')

export const formatISOToLongDate = (date: string) => formatISO(date, 'PP')

export const formatISOToShortDate = (date: string) => formatISO(date, 'Pp')

export const formatISOToFullHumanReadableDate = (date: string | Date) =>
  formatISO(date, 'MMMM d, yyyy')

export const formatISOToRelativeToNow = (
  isoDate: string,
  options: {
    includeSeconds?: boolean
    addSuffix?: boolean
    locale?: Locale
  } = { addSuffix: true },
) => formatRelative(parseISO(isoDate), new Date(), options)

export const formatISODistanceToNow = (
  isoDate: string,
  options: {
    includeSeconds?: boolean
    addSuffix?: boolean
    locale?: Locale
  } = { addSuffix: true },
) => formatDistanceToNow(parseISO(isoDate), options)

export const formatISOToHumanReadableDate = (
  date: string | Date,
  { includeYear }: { includeYear: boolean } = { includeYear: true },
) => formatISO(date, `MMM d${includeYear ? ', yyyy' : ''}`)

export const getCurrentNamedTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Parses an ISO date string and returns a Date object with only the date portion (no time).
 * This function removes any timezone information from the ISO string and creates a date
 * object representing midnight on the specified date.
 *
 * @param isoString - The ISO 8601 formatted date string to parse
 * @returns A Date object representing midnight on the specified date
 */
export const parseISOStringDateOnly = (isoString: string): Date => {
  const dateWithoutTimezone = isoString.replace(TIMEZONE_SUFFIX, '')
  const dateAtMidnight = new Date(dateWithoutTimezone)

  return dateAtMidnight
}
